import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { getCurrentUser, isAdminUser } from "../utils/auth";
import { fetchScopedCollections, toDate, parseIsoDate } from "../utils/scopeUtils";

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function endOfDay(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  );
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function startOfWeek(date) {
  const day = (date.getDay() + 6) % 7; // Montag = 0
  return startOfDay(addDays(date, -day));
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfYear(date) {
  return new Date(date.getFullYear(), 0, 1);
}

function isWithinRange(date, start, end) {
  return date >= start && date <= end;
}

function buildClampedDate(year, monthIndex, day, sourceTime) {
  const lastDayOfMonth = new Date(year, monthIndex + 1, 0).getDate();
  const safeDay = Math.min(day, lastDayOfMonth);

  return new Date(
    year,
    monthIndex,
    safeDay,
    sourceTime.getHours(),
    sourceTime.getMinutes(),
    sourceTime.getSeconds(),
    sourceTime.getMilliseconds()
  );
}

function getRangeBounds(range) {
  const now = new Date();

  if (range === "Heute") {
    const start = startOfDay(now);
    const end = now;
    const previousEnd = addDays(now, -1);
    const previousStart = startOfDay(previousEnd);
    return { start, end, previousStart, previousEnd };
  }

  if (range === "Wöchentlich") {
    const start = startOfWeek(now);
    const end = now;
    const previousEnd = addDays(now, -7);
    const previousStart = startOfWeek(previousEnd);
    return { start, end, previousStart, previousEnd };
  }

  if (range === "Monatlich") {
    const start = startOfMonth(now);
    const end = now;
    const previousEnd = buildClampedDate(
      now.getFullYear(),
      now.getMonth() - 1,
      now.getDate(),
      now
    );
    const previousStart = startOfMonth(previousEnd);
    return { start, end, previousStart, previousEnd };
  }

  const start = startOfYear(now);
  const end = now;
  const previousEnd = buildClampedDate(
    now.getFullYear() - 1,
    now.getMonth(),
    now.getDate(),
    now
  );
  const previousStart = startOfYear(previousEnd);

  return { start, end, previousStart, previousEnd };
}

function countHighStress(stressLogs) {
  return stressLogs.filter((log) => Number(log.stressLevel || 0) >= 4).length;
}

function countOpenTasks(tasks) {
  return tasks.filter((task) => task.done !== true).length;
}

function sumFocusMinutes(focusSessions) {
  return focusSessions.reduce(
    (sum, session) => sum + Number(session.minutes || 0),
    0
  );
}

function countActiveUsers(users, start, end) {
  return users.filter((user) => {
    const lastActive = toDate(user.lastActiveAt);
    return lastActive && isWithinRange(lastActive, start, end);
  }).length;
}

function calcStressDistribution(stressLogs, start, end) {
  const filtered = stressLogs.filter((log) => {
    const d =
      parseIsoDate(log.date) ||
      toDate(log.updatedAt) ||
      toDate(log.createdAt);
    return d && isWithinRange(d, start, end);
  });

  if (filtered.length === 0) {
    return {
      motiviert: 0,
      ruhig: 0,
      belastet: 0,
      averageStress: 0,
    };
  }

  let motiviert = 0;
  let ruhig = 0;
  let belastet = 0;
  let total = 0;

  filtered.forEach((log) => {
    const level = Number(log.stressLevel || 0);
    total += level;

    if (level <= 2) motiviert += 1;
    else if (level === 3) ruhig += 1;
    else belastet += 1;
  });

  const count = filtered.length;

  return {
    motiviert: Math.round((motiviert / count) * 100),
    ruhig: Math.round((ruhig / count) * 100),
    belastet: Math.round((belastet / count) * 100),
    averageStress: Number((total / count).toFixed(1)),
  };
}

function buildBuckets(range, start) {
  if (range === "Heute") {
    return Array.from({ length: 7 }, (_, i) => {
      const hour = 8 + i * 2;
      const from = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
        hour,
        0,
        0,
        0
      );
      const to = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
        hour + 1,
        59,
        59,
        999
      );

      return {
        label: String(hour).padStart(2, "0"),
        from,
        to,
      };
    });
  }

  if (range === "Wöchentlich") {
    const labels = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

    return Array.from({ length: 7 }, (_, i) => {
      const from = startOfDay(addDays(start, i));
      const to = endOfDay(from);

      return {
        label: labels[i],
        from,
        to,
      };
    });
  }

  if (range === "Monatlich") {
    const year = start.getFullYear();
    const month = start.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const buckets = [];

    for (let day = 1, week = 1; day <= daysInMonth; day += 7, week += 1) {
      const from = new Date(year, month, day, 0, 0, 0, 0);
      const to = new Date(
        year,
        month,
        Math.min(day + 6, daysInMonth),
        23,
        59,
        59,
        999
      );

      buckets.push({
        label: `W${week}`,
        from,
        to,
      });
    }

    return buckets;
  }

  const labels = [
    "Jan",
    "Feb",
    "Mär",
    "Apr",
    "Mai",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Okt",
    "Nov",
    "Dez",
  ];

  return Array.from({ length: 12 }, (_, i) => {
    const from = new Date(start.getFullYear(), i, 1, 0, 0, 0, 0);
    const to = new Date(start.getFullYear(), i + 1, 0, 23, 59, 59, 999);

    return {
      label: labels[i],
      from,
      to,
    };
  });
}

function buildChartSeries(range, key, current, previous, users) {
  const { start, previousStart } = getRangeBounds(range);
  const currentBuckets = buildBuckets(range, start);
  const previousBuckets = buildBuckets(range, previousStart);

  function valueForBucket(metricKey, bucket, source, usersSource) {
    if (metricKey === "activeUsers") {
      return usersSource.filter((user) => {
        const lastActive = toDate(user.lastActiveAt);
        return lastActive && isWithinRange(lastActive, bucket.from, bucket.to);
      }).length;
    }

    if (metricKey === "highStress") {
      return source.filter((item) => {
        const d =
          parseIsoDate(item.date) ||
          toDate(item.updatedAt) ||
          toDate(item.createdAt);
        return (
          d &&
          isWithinRange(d, bucket.from, bucket.to) &&
          Number(item.stressLevel || 0) >= 4
        );
      }).length;
    }

    if (metricKey === "focusMinutes") {
      return source
        .filter((item) => {
          const d =
            parseIsoDate(item.date) ||
            toDate(item.finishedAt) ||
            toDate(item.updatedAt);
          return d && isWithinRange(d, bucket.from, bucket.to);
        })
        .reduce((sum, item) => sum + Number(item.minutes || 0), 0);
    }

    if (metricKey === "openTasks") {
      return source.filter((item) => {
        const d =
          parseIsoDate(item.date) ||
          toDate(item.updatedAt) ||
          toDate(item.createdAt);
        return d && isWithinRange(d, bucket.from, bucket.to) && item.done !== true;
      }).length;
    }

    return 0;
  }

  const maxLength = Math.max(currentBuckets.length, previousBuckets.length);

  return Array.from({ length: maxLength }, (_, index) => {
    const currentBucket = currentBuckets[index];
    const previousBucket = previousBuckets[index];

    return {
      label: currentBucket?.label || previousBucket?.label || `W${index + 1}`,
      value: currentBucket
        ? valueForBucket(
            key,
            currentBucket,
            key === "activeUsers" ? [] : current,
            users.current
          )
        : 0,
      value2: previousBucket
        ? valueForBucket(
            key,
            previousBucket,
            key === "activeUsers" ? [] : previous,
            users.previous
          )
        : 0,
    };
  });
}

function buildTrend(currentValue, previousValue, inverted = false) {
  if (!previousValue) {
    return { dir: currentValue >= 0 ? "up" : "down", pct: 0 };
  }

  const diff = currentValue - previousValue;
  const pct = Math.round((Math.abs(diff) / Math.max(previousValue, 1)) * 100);

  if (inverted) {
    return {
      dir: diff <= 0 ? "up" : "down",
      pct,
    };
  }

  return {
    dir: diff >= 0 ? "up" : "down",
    pct,
  };
}

function detectHighRiskUsers(users, stressLogs, tasks) {
  const byUser = {};

  users.forEach((user) => {
    byUser[user.id] = {
      user,
      stress: [],
      tasks: [],
    };
  });

  stressLogs.forEach((log) => {
    if (!log.userId) return;
    if (!byUser[log.userId]) {
      byUser[log.userId] = { user: null, stress: [], tasks: [] };
    }
    byUser[log.userId].stress.push(log);
  });

  tasks.forEach((task) => {
    if (!task.userId) return;
    if (!byUser[task.userId]) {
      byUser[task.userId] = { user: null, stress: [], tasks: [] };
    }
    byUser[task.userId].tasks.push(task);
  });

  const riskyProfiles = [];

  Object.entries(byUser).forEach(([uid, data]) => {
    const highStressCount = data.stress.filter(
      (log) => Number(log.stressLevel || 0) >= 4
    ).length;

    const openTasks = data.tasks.filter((task) => task.done !== true).length;

    if (highStressCount >= 3 || (highStressCount >= 2 && openTasks >= 5)) {
      riskyProfiles.push({
        uid,
        name:
          data.user?.displayName ||
          data.user?.name ||
          data.user?.email ||
          "Unbekannter User",
        className: data.user?.className || "",
        highStressCount,
        openTasks,
      });
    }
  });

  return riskyProfiles;
}

function buildActions({
  highStress,
  openTasks,
  focusMinutes,
  averageStress,
  riskUsers,
  pendingAccessRequests,
}) {
  const rows = [];

  if (riskUsers.length > 0) {
    rows.push({
      type: "Stress",
      desc: `${riskUsers.length} Profile wirken aktuell besonders belastet.`,
      status: "Sofort prüfen",
      statusTone: "is-warm",
      action: "Zu Usern",
      go: "/users-classes/users",
    });
  }

  if (highStress > 0) {
    rows.push({
      type: "Belastung",
      desc: `${highStress} auffällige Stressfälle wurden in diesem Zeitraum erkannt.`,
      status: "Prüfen",
      statusTone: "is-warm",
      action: "Statistiken öffnen",
      go: "/stats",
    });
  }

  if (openTasks > 0) {
    rows.push({
      type: "Aufgaben",
      desc: `${openTasks} offene Aufgaben können auf Überforderung oder fehlende Struktur hinweisen.`,
      status: "Beobachten",
      statusTone: "is-info",
      action: "Zu Statistiken",
      go: "/stats",
    });
  }

  if (focusMinutes > 0) {
    rows.push({
      type: "Fokus",
      desc: `Es wurden ${focusMinutes.toLocaleString("de-AT")} Fokusminuten erfasst.`,
      status: "Stabil",
      statusTone: "is-soft",
      action: "Details öffnen",
      go: "/stats",
    });
  }

  if (averageStress >= 4) {
    rows.push({
      type: "Warnung",
      desc: `Der durchschnittliche Stresswert liegt aktuell bei ${averageStress}.`,
      status: "Handeln",
      statusTone: "is-warm",
      action: "Benachrichtigen",
      go: "/notifications",
    });
  }

  if (pendingAccessRequests > 0) {
    rows.push({
      type: "Zugang",
      desc: `${pendingAccessRequests} offene Zugangsanfragen warten auf Bearbeitung.`,
      status: "Neu",
      statusTone: "is-info",
      action: "Anfragen öffnen",
      go: "/access-requests",
    });
  }

  return rows.slice(0, 5);
}

function buildActivities(users, pendingAccessRequests) {
  const userActivities = users
    .filter((user) => user.lastActiveAt)
    .sort((a, b) => {
      const da = toDate(a.lastActiveAt)?.getTime() || 0;
      const db = toDate(b.lastActiveAt)?.getTime() || 0;
      return db - da;
    })
    .slice(0, 4)
    .map((user) => {
      const name = user.displayName || user.name || user.email || "Unbekannter User";
      const className = user.className ? ` · ${user.className}` : "";
      return `${name}${className} war zuletzt aktiv.`;
    });

  if (pendingAccessRequests > 0) {
    userActivities.unshift(
      `${pendingAccessRequests} neue Zugangsanfrage(n) warten auf Freigabe.`
    );
  }

  return userActivities.slice(0, 5);
}

function buildGreeting(stats, pendingAccessRequests) {
  return {
    activeUsers: stats.activeUsers,
    highStress: stats.highStress,
    focusMinutes: stats.focusMinutes,
    openTasks: stats.openTasks,
    pendingAccessRequests,
  };
}

export async function getDashboardData(range = "Wöchentlich") {
  const currentUser = getCurrentUser();
  const isAdmin = isAdminUser(currentUser);
  const { start, end, previousStart, previousEnd } = getRangeBounds(range);

  const scopedCurrent = await fetchScopedCollections();
  const scopedPrevious = await fetchScopedCollections();

  const users = scopedCurrent.users;
  const tasks = scopedCurrent.tasks;
  const stressLogs = scopedCurrent.stressLogs;
  const focusSessions = scopedCurrent.focusSessions;

  const previousTasks = scopedPrevious.tasks.filter((item) => {
    const d =
      parseIsoDate(item.date) ||
      toDate(item.updatedAt) ||
      toDate(item.createdAt);
    return d && isWithinRange(d, previousStart, previousEnd);
  });

  const previousStress = scopedPrevious.stressLogs.filter((item) => {
    const d =
      parseIsoDate(item.date) ||
      toDate(item.updatedAt) ||
      toDate(item.createdAt);
    return d && isWithinRange(d, previousStart, previousEnd);
  });

  const previousFocus = scopedPrevious.focusSessions.filter((item) => {
    const d =
      parseIsoDate(item.date) ||
      toDate(item.finishedAt) ||
      toDate(item.updatedAt);
    return d && isWithinRange(d, previousStart, previousEnd);
  });

  const currentTasks = tasks.filter((item) => {
    const d =
      parseIsoDate(item.date) ||
      toDate(item.updatedAt) ||
      toDate(item.createdAt);
    return d && isWithinRange(d, start, end);
  });

  const currentStress = stressLogs.filter((item) => {
    const d =
      parseIsoDate(item.date) ||
      toDate(item.updatedAt) ||
      toDate(item.createdAt);
    return d && isWithinRange(d, start, end);
  });

  const currentFocus = focusSessions.filter((item) => {
    const d =
      parseIsoDate(item.date) ||
      toDate(item.finishedAt) ||
      toDate(item.updatedAt);
    return d && isWithinRange(d, start, end);
  });

  const currentUsers = users.filter((user) => {
    const d = toDate(user.lastActiveAt);
    return d && isWithinRange(d, start, end);
  });

  const previousUsers = users.filter((user) => {
    const d = toDate(user.lastActiveAt);
    return d && isWithinRange(d, previousStart, previousEnd);
  });

  let pendingAccessRequests = 0;

  if (isAdmin) {
    const accessRequestsSnap = await getDocs(collection(db, "accessRequests"));
    const accessRequests = accessRequestsSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    pendingAccessRequests = accessRequests.filter(
      (item) => item.status === "pending"
    ).length;
  }

  const currentStats = {
    activeUsers: countActiveUsers(users, start, end),
    highStress: countHighStress(currentStress),
    focusMinutes: sumFocusMinutes(currentFocus),
    openTasks: countOpenTasks(currentTasks),
  };

  const previousStats = {
    activeUsers: countActiveUsers(users, previousStart, previousEnd),
    highStress: countHighStress(previousStress),
    focusMinutes: sumFocusMinutes(previousFocus),
    openTasks: countOpenTasks(previousTasks),
  };

  const stressDistribution = calcStressDistribution(stressLogs, start, end);
  const riskUsers = detectHighRiskUsers(users, stressLogs, tasks);

  const chartDataByKpi = {
    activeUsers: buildChartSeries(
      range,
      "activeUsers",
      [],
      [],
      { current: currentUsers, previous: previousUsers }
    ),
    highStress: buildChartSeries(
      range,
      "highStress",
      currentStress,
      previousStress,
      { current: currentUsers, previous: previousUsers }
    ),
    focusMinutes: buildChartSeries(
      range,
      "focusMinutes",
      currentFocus,
      previousFocus,
      { current: currentUsers, previous: previousUsers }
    ),
    openTasks: buildChartSeries(
      range,
      "openTasks",
      currentTasks,
      previousTasks,
      { current: currentUsers, previous: previousUsers }
    ),
  };

  const rawActions = buildActions({
    ...currentStats,
    averageStress: stressDistribution.averageStress,
    riskUsers,
    pendingAccessRequests,
  });

  const rawActivities = buildActivities(users, pendingAccessRequests);

  return {
    stats: currentStats,
    greeting: buildGreeting(currentStats, pendingAccessRequests),
    trends: {
      activeUsers: buildTrend(currentStats.activeUsers, previousStats.activeUsers),
      highStress: buildTrend(currentStats.highStress, previousStats.highStress, true),
      focusMinutes: buildTrend(currentStats.focusMinutes, previousStats.focusMinutes),
      openTasks: buildTrend(currentStats.openTasks, previousStats.openTasks, true),
    },
    stressDistribution,
    riskUsers,
    pendingAccessRequests,
    chartDataByKpi,
    activities: isAdmin
      ? rawActivities
      : rawActivities.filter((item) => !String(item).includes("Zugangsanfrage")),
    actions: isAdmin
      ? rawActions
      : rawActions.filter(
          (item) => item.go !== "/access-requests" && item.go !== "/notifications"
        ),
  };
}